import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CSSProperties, ReactNode } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProposalProductItemType } from '@econtractor/types';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { CountBadge } from 'modules/warehouse';
import FormInput from 'components/forms/FormInput';
import SortableDragHandle from '../SortableDragHandle';
import {
    formatPrimaryPrice,
    formatRateLine,
    getSaleUsd,
    shouldShowRateLine,
} from '../model/templatePriceUtils';
import type useProductsTable from './model/useProductsTable';

type ProductsTableApi = ReturnType<typeof useProductsTable>;

export interface ProductsTableRowProps {
    row: ProposalProductItemType;
    index: number;
    rate: number;
    localeNs: string;
    disabled: boolean;
    table: ProductsTableApi;
    sortableId?: string;
}

interface ProductsTableRowViewProps extends Omit<ProductsTableRowProps, 'sortableId'> {
    dragHandle?: ReactNode;
    rowRef?: (node: HTMLElement | null) => void;
    rowStyle?: CSSProperties;
    rowClassName?: string;
}

const ProductsTableRowView = ({
    row,
    index,
    rate,
    localeNs,
    disabled,
    table,
    dragHandle,
    rowRef,
    rowStyle,
    rowClassName,
}: ProductsTableRowViewProps) => {
    const { t } = useTranslation([localeNs, 'common']);
    const {
        editNameIndex,
        editPriceIndex,
        editCountIndex,
        setEditNameIndex,
        setEditPriceIndex,
        setEditCountIndex,
        openNameEdit,
        openPriceEdit,
        savePriceEdit,
        openCountEdit,
        saveCountEdit,
        updateRow,
        removeRow,
        clearInlineEdits,
    } = table;

    return (
        <tr ref={rowRef} style={rowStyle} className={rowClassName}>
            {dragHandle ? <td className="drag">{dragHandle}</td> : null}
            <td className="name" data-label={t('edit.products.tableProduct')}>
                {editNameIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditNameIndex(null);
                        }}
                        defaultValue={row.prodName || ''}
                        callBack={(value) => {
                            const nextName = String(value || '').trim();
                            if (nextName) {
                                updateRow(index, { prodName: nextName });
                            }
                            setEditNameIndex(null);
                        }}
                    />
                ) : (
                    <>
                        <div
                            className={`fw-medium ${!disabled ? 'cursor-pointer' : ''}`}
                            onClick={() => !disabled && openNameEdit(index)}
                        >
                            {row.prodName || row.sourceName || (row.asSetUnit ? t('edit.products.modeSet') : `#${row.productId}`)}
                        </div>
                        <small className="text-primary">{row.vendorName || row.prodArticle || ''}</small>
                    </>
                )}
            </td>
            <td className="source" data-label={t('edit.products.tableSource')}>
                {row.sourceType === 'set' ? (
                    <span className="badge bg-light-primary">{row.sourceName || t('edit.products.modeSet')}</span>
                ) : (
                    <span className="text-muted">{t('edit.products.modeProduct')}</span>
                )}
            </td>
            <td className="price" data-label={t('edit.products.tablePrice')}>
                {editPriceIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditPriceIndex(null);
                        }}
                        defaultValue={getSaleUsd(row)}
                        decimalPlaces={2}
                        callBack={(value) => {
                            savePriceEdit(index, value);
                        }}
                    />
                ) : (
                    <>
                        <p
                            className={`text-muted f-12 mb-0 my-1 ${!disabled ? 'cursor-pointer text-break' : ''}`}
                            onClick={() => !disabled && openPriceEdit(index)}
                        >
                            {formatPrimaryPrice(getSaleUsd(row), rate)}
                        </p>
                        {shouldShowRateLine(rate) && (
                            <p className="text-muted f-12 mb-0 my-1">
                                {formatRateLine(getSaleUsd(row), rate)}
                            </p>
                        )}
                    </>
                )}
            </td>
            <td
                className={`count text-end fw-medium ${Number(row.itemCount || 0) < 0 ? '_text-danger' : ''}`}
                data-label={t('edit.products.tableCount')}
            >
                {editCountIndex === index && !disabled ? (
                    <FormInput
                        compact
                        showForm
                        setShowForm={(show) => {
                            if (!show) setEditCountIndex(null);
                        }}
                        defaultValue={Number(row.itemCount || 0)}
                        decimalPlaces={2}
                        callBack={(value) => saveCountEdit(index, value)}
                    />
                ) : (
                    <div className="d-flex justify-content-end align-items-center">
                        <div
                            onClick={() => !disabled && openCountEdit(index)}
                            className={!disabled ? 'cursor-pointer' : ''}
                        >
                            <CountBadge count={safeFloatParse(row.itemCount || 0) || 0} />
                        </div>
                    </div>
                )}
            </td>
            {!disabled ? (
                <td className="action text-end" data-label={t('edit.products.tableActions')}>
                    <Button
                        type="button"
                        size="sm"
                        className="py-0 px-1 mb-1"
                        variant="light-danger"
                        onClick={() => {
                            clearInlineEdits();
                            removeRow(index);
                        }}
                    >
                        <i className="bi bi-trash3"></i>
                    </Button>
                </td>
            ) : null}
        </tr>
    );
};

const SortableProductsTableRow = ({ sortableId, ...props }: ProductsTableRowProps & { sortableId: string }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: sortableId,
    });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <ProductsTableRowView
            {...props}
            rowRef={setNodeRef}
            rowStyle={style}
            rowClassName={isDragging ? 'sortable-row--dragging' : undefined}
            dragHandle={<SortableDragHandle attributes={attributes} listeners={listeners} />}
        />
    );
};

const ProductsTableRow = ({ sortableId, ...props }: ProductsTableRowProps) => {
    if (sortableId) {
        return <SortableProductsTableRow sortableId={sortableId} {...props} />;
    }
    return <ProductsTableRowView {...props} />;
};

export default ProductsTableRow;
